import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const EFFECTIVE_DATE = '2026-05-22';
const CONTACT = 'aitookmyjobagain@gmail.com';
// Tracks the entity behind the service. Currently a California sole
// proprietorship. If/when an LLC is formed, update this to the LLC's
// state of registration (and adjust the venue sentence below if the
// LLC operates from a different state).
const JURISDICTION = 'State of California, United States';

const TermsPage: React.FC = () => {
  useDocumentTitle('terms of service');
  return (
    <div className="legal-page">
      <Link to="/" className="legal-back">← back to study buddy</Link>
      <h1>Terms of Service</h1>
      <div className="legal-meta">Effective {EFFECTIVE_DATE}</div>

      <p>
        Study Buddy is a free service for practicing coding-interview
        problems. By creating an account, you agree to these terms.
      </p>

      <h2>1. The service</h2>
      <p>
        Study Buddy provides an in-browser coding environment, a
        catalogue of practice problems, progress tracking, and social
        features (friends, leaderboards). It runs as a web app at
        study buddy's URL.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You need to be 13 or older to create an account.</li>
        <li>
          You're responsible for the activity on your account. Don't
          share credentials. If you suspect your account has been
          accessed by someone else, change your password immediately
          and contact us.
        </li>
        <li>
          One person, one account. Operating multiple accounts to game
          leaderboards or trophies will get them all suspended.
        </li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree NOT to:</p>
      <ul>
        <li>
          Scrape, mirror, or republish the problem catalogue. It's the
          product.
        </li>
        <li>
          Attempt to disrupt the service (DoS, abuse rate limits,
          exploit bugs in ways that interfere with other users).
        </li>
        <li>
          Use the service to send spam, harass other users, post
          unlawful content, or impersonate anyone.
        </li>
        <li>
          Use the service in any way that violates applicable laws,
          including intellectual-property law.
        </li>
        <li>
          Build a competing product by automating account creation or
          systematically downloading content from this service.
        </li>
      </ul>

      <h2>4. Your content</h2>
      <p>
        Code you write in the IDE stays in your browser. We don't store
        it server-side. The handle you pick and your public profile
        information (level, solve count, etc.) are visible to other
        users.
      </p>

      <h2>5. Service availability</h2>
      <p>
        We aim for high availability but don't guarantee uptime. The
        service is provided "as is" without warranty. We may, with
        reasonable notice, change features, add limits, or
        discontinue parts of the service.
      </p>

      <h2>6. Account termination</h2>
      <ul>
        <li>
          <strong>By you:</strong> close your account at any time from
          your profile page. Data is wiped per our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </li>
        <li>
          <strong>By us:</strong> we may suspend or terminate accounts
          that violate these terms (notably the "acceptable use"
          section). For non-egregious violations we'll send a warning
          first; for fraud, harassment, or abuse, suspension is immediate.
        </li>
      </ul>

      <h2>7. Disclaimers</h2>
      <p>
        Study Buddy is an educational tool, not a guarantee of interview
        success. We make no claims about which problems will appear on
        any specific interview. Reference solutions are provided as
        learning aids and may not be optimal.
      </p>
      <p>
        To the maximum extent permitted by law, the service is provided
        "as is" and without warranty of any kind. We're not liable for
        any indirect, incidental, or consequential damages arising from
        your use of the service.
      </p>

      <h2>8. Governing law and venue</h2>
      <p>
        These terms are governed by the laws of the {JURISDICTION},
        without regard to its conflict-of-law principles. Any dispute
        arising out of or relating to these terms or the service shall
        be brought exclusively in the state or federal courts located
        in the State of California, and you consent to the personal
        jurisdiction of those courts.
      </p>

      <h2>9. Changes to these terms</h2>
      <p>
        We may update these terms. We'll bump the effective date at the
        top of this page. Material changes (new restrictions, new fees,
        etc.) will be announced by email to signed-in users at least
        14 days in advance.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions? Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>
    </div>
  );
};

export default TermsPage;
